import React, { useEffect, useRef, useState } from 'react';

import {
  PAGE_HASHTAG_TYPE,
  PAGE_HASHTAG_TYPE_VALUE,
} from '@typings/setting/pageTagType.interface';
import styled, { css } from 'styled-components';

const tabBtnType = {
  [PAGE_HASHTAG_TYPE.HORIZONTAL]: css`
    &.tabActive {
      font-weight: 800;
    }
  `,
  [PAGE_HASHTAG_TYPE.VERTICAL]: css`
    &.tabActive {
      font-weight: 800;
    }
  `,
  [PAGE_HASHTAG_TYPE.SPREAD]: css`
    padding: 6px;
    margin: 0px 0px 8px 0px;
    color: var(--textColor);
    background-color: var(--subColor);
    &.tabActive {
      color: var(--buttonTextColor);
      background-color: var(--buttonBackgroundColor);
      font-weight: 800;
    }
  `,
};

const TabButtonWrap = styled.button<{
  layout: PAGE_HASHTAG_TYPE_VALUE;
  $isImage?: boolean;
}>`
  font-family: var(--defaultFontFamily);
  color: var(--subTextColor);
  padding: 8px;
  margin: 9px 0 12px;
  transition: 0.2s;
  position: relative;
  font-size: 14px;
  white-space: pre;
  border-radius: 8px;
  font-size: 14px;
  ${({ layout }) => layout && tabBtnType[layout]}

  &:hover {
    font-weight: 700;
    background-color: ${({ $isImage }) =>
    $isImage ? 'transparent' : 'var(--inkColor)'};
  }

  ${({ $isImage }) =>
    $isImage &&
    css`
      &:hover .img-hover {
        opacity: 1;
      }
    `}

  &.tabCustomActive {
    font-weight: 800;

    &::after {
      display: ${({ $isImage }) => ($isImage ? 'none' : 'block')};
      content: '';
      position: absolute;
      left: 0;
      bottom: -6px;
      width: 100%;
      border-bottom: 4px solid var(--primaryColor);
    }
  }

  @media (max-width: 480px) {
    font-size: 13px;
    padding: 8px;
  }
`;

const ImgTabImgWrapper = styled.div`
  display: inline-grid;

  .img-normal,
  .img-hover {
    grid-area: 1 / 1;
    display: block;
    object-fit: contain;
    transition: opacity 0.2s ease;
  }

  .img-hover {
    opacity: 0;
    pointer-events: none;
  }

  @media (max-width: 480px) {
    .img-normal,
    .img-hover {
      max-height: 48px !important;
    }
  }
`;

type Props = {
  children: any;
  id: string;
  onClick: (e) => void;
  className?: string;
  style?: any;
  layout?: PAGE_HASHTAG_TYPE_VALUE;
  isImage?: boolean;
  imgURL?: string;
  hoverImgURL?: string;
  imgAlt?: string;
  imgMaxHeight?: number;
};

function TabButton({
  children,
  id,
  onClick,
  className,
  style,
  layout = PAGE_HASHTAG_TYPE.HORIZONTAL,
  isImage = false,
  imgURL,
  hoverImgURL,
  imgAlt,
  imgMaxHeight = 80,
}: Props) {
  const [hoverImgLoaded, setHoverImgLoaded] = useState(false);
  const [resolvedHoverSrc, setResolvedHoverSrc] = useState<string>(
    hoverImgURL || imgURL || '',
  );
  const hoverImgRef = useRef<HTMLImageElement>(null);
  const hasHoverSource = !!hoverImgURL;
  const hasHoverEffect =
    hasHoverSource && hoverImgLoaded && resolvedHoverSrc !== (imgURL || '');

  useEffect(() => {
    if (!hasHoverSource) {
      setHoverImgLoaded(false);
      setResolvedHoverSrc(imgURL || '');
      return;
    }
    setResolvedHoverSrc(hoverImgURL || '');
    const img = hoverImgRef.current;
    if (img?.complete) {
      if (img.naturalWidth > 0) {
        setHoverImgLoaded(true);
      } else {
        setResolvedHoverSrc(imgURL || '');
        setHoverImgLoaded(true);
      }
    }
  }, [hasHoverSource, hoverImgURL, imgURL]);

  const renderImage = !!imgURL;

  return (
    <TabButtonWrap
      style={style}
      id={id}
      onClick={onClick}
      className={className}
      layout={layout}
      $isImage={isImage && (renderImage ? hasHoverEffect : false)}
    >
      {renderImage ? (
        <ImgTabImgWrapper>
          <img
            className="img-normal"
            src={imgURL}
            alt={imgAlt || ''}
            style={{ maxHeight: imgMaxHeight }}
          />
          {hasHoverSource && (
            <img
              ref={hoverImgRef}
              className="img-hover"
              src={resolvedHoverSrc}
              alt={imgAlt || ''}
              style={{ maxHeight: imgMaxHeight }}
              onLoad={() => setHoverImgLoaded(true)}
              onError={() => {
                setResolvedHoverSrc(imgURL || '');
                setHoverImgLoaded(true);
              }}
            />
          )}
        </ImgTabImgWrapper>
      ) : (
        children
      )}
    </TabButtonWrap>
  );
}

export default TabButton;
